import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyNasajplMode({ brand, today, Copy }: ModeRendererProps) {
  // Namespaced IDs to avoid collisions
  const idBase = React.useMemo(() => {
    try {
      const url = typeof window !== "undefined" ? new URL(window.location.href) : null;
      const param = url
        ? url.searchParams.get("id") || url.searchParams.get("uid") || url.searchParams.get("modeId") || url.searchParams.get("mode_id")
        : null;
      return param ? `nasajpl_${param}` : `nasajpl_${Math.random().toString(36).slice(2, 8)}`;
    } catch {
      return `nasajpl_${Math.random().toString(36).slice(2, 8)}`;
    }
  }, []);
  const gradId = `${idBase}_grad`;
  const glowId = `${idBase}_glow`;

  const defs = (
    <defs>
      <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0B3D91" />
        <stop offset="50%" stopColor="#FC3D21" />
        <stop offset="100%" stopColor="#FFD700" />
      </linearGradient>
      <radialGradient id={glowId} cx="50%" cy="50%">
        <stop offset="0%" stopColor="#FFD700" stopOpacity={0.8} />
        <stop offset="100%" stopColor="#0B3D91" stopOpacity={0} />
      </radialGradient>
    </defs>
  );

  // Telemetry bands: data transmission layers
  const telemetryBands = [];
  for (let i = 0; i < 16; i++) {
    const y = 100 + i * 58;
    const signal = Math.sin(i * 0.5) * 30;
    const points = [];
    for (let j = 0; j < 24; j++) {
      const x = 50 + j * 30;
      const wave = Math.sin(j * 0.4 + i * 0.2) * signal;
      points.push(`${x},${y + wave}`);
    }
    telemetryBands.push(
      <polyline
        key={`band-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={1.5}
        opacity={0.4}
      />
    );
  }

  // Trajectory arcs: mission paths
  const trajectoryArcs = [];
  for (let i = 0; i < 10; i++) {
    const startAngle = (i * Math.PI * 2) / 10;
    const radius = 150 + i * 25;
    const path = [];
    for (let j = 0; j < 18; j++) {
      const angle = startAngle + (j * Math.PI) / 18;
      const x = 397 + radius * Math.cos(angle);
      const y = 561.5 + radius * Math.sin(angle);
      path.push(`${x},${y}`);
    }
    trajectoryArcs.push(
      <polyline
        key={`traj-${i}`}
        points={path.join(" ")}
        fill="none"
          stroke={`url(#${gradId})`}
        strokeWidth={2}
        opacity={0.3}
        strokeDasharray="8,4"
      />
    );
  }

  // Starfield matrices: deep space background
  const starfieldMatrices = [];
  for (let i = 0; i < 60; i++) {
    const x = 70 + Math.random() * 654;
    const y = 100 + Math.random() * 900;
    const size = 1 + Math.random() * 2;
    starfieldMatrices.push(
      <circle
        key={`star-${i}`}
        cx={x}
        cy={y}
        r={size}
        fill="#FFD700"
        opacity={0.3 + Math.random() * 0.5}
      />
    );
  }

  // Navigation grid: coordinate system
  const navigationGrid = [];
  for (let i = 0; i < 14; i++) {
    const x = 80 + i * 52;
    navigationGrid.push(
      <line
        key={`nav-v-${i}`}
        x1={x}
        y1={100}
        x2={x}
        y2={1023}
        stroke="#FC3D21"
        strokeWidth={0.5}
        opacity={0.2}
        strokeDasharray="2,6"
      />
    );
  }
  for (let i = 0; i < 18; i++) {
    const y = 100 + i * 52;
    navigationGrid.push(
      <line
        key={`nav-h-${i}`}
        x1={70}
        y1={y}
        x2={724}
        y2={y}
        stroke="#FC3D21"
        strokeWidth={0.5}
        opacity={0.2}
        strokeDasharray="2,6"
      />
    );
  }

  // Mission control center
  const missionCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={100} fill="url(#starGlow)" />
      <circle cx={397} cy={561.5} r={70} fill="none" stroke="#FFD700" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={50} fill="none" stroke="#FC3D21" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={30} fill="none" stroke="#0B3D91" strokeWidth={2} />
      {/* Crosshair */}
      <line x1={327} y1={561.5} x2={467} y2={561.5} stroke="#FFD700" strokeWidth={2} />
      <line x1={397} y1={491.5} x2={397} y2={631.5} stroke="#FFD700" strokeWidth={2} />
    </g>
  );

  return (
    <g>
      {defs}
      {starfieldMatrices}
      {navigationGrid}
      {telemetryBands}
      {trajectoryArcs}
      {missionCore}
      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">
          {Copy?.title}
        </text>
        <text x={0} y={22} fontSize={14}>
          {Copy?.subtitle}
        </text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>
          {Copy?.date}
        </text>
      </g>
    </g>
  );
}
